import React, { useEffect, useState } from 'react';
import { Button, Modal, Spin, Tabs, Pagination } from 'antd';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { TextVisitServiceType, TPrescriptionListItem } from 'types/textVisit';
import { getTreatmentPlanAppointmentCounts, getTreatmentPlanItemList } from 'api/home';
import { getRecheckAppointments } from 'api/eligibility';
import { TreatmentPlanItemVo, TTreatmentPlanAppointmentQueryReq } from 'types/home';
import { TRecheckAppointmentVo } from 'types/insuranceEligibility';
import { PATH } from 'constants/path';
import useTimezone from 'hooks/useTimezone';
import TreatmentPlanCard from 'pages/dashboard/ProspectsDetailPage/components/treatmentPlan/TreatmentPlanCard';
import VideoVisitUnSubmittedDrawer from 'components/VideoVisitUnSubmittedDrawer';
import TextVisitUnSubmittedDrawer from 'components/TextVisitUnSubmittedDrawer';
import TextVisitSubmittedDrawer from 'components/TextVisitSubmittedDrawer';
import s from './TodoListModal.module.less';

dayjs.extend(utc);

export type TTodoTab = 'recheck' | 'inProgress' | 'overdue';

// 日期/时钟小图标（与 TreatmentPlanCard 同形，其未导出）
const CalendarIcon: React.FC = () => (
    <svg width="16" height="16" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M6 5.25V2.25M12 5.25V2.25M5.25 8.25H12.75M3.75 15.75H14.25C15.0784 15.75 15.75 15.0784 15.75 14.25V5.25C15.75 4.42157 15.0784 3.75 14.25 3.75H3.75C2.92157 3.75 2.25 4.42157 2.25 5.25V14.25C2.25 15.0784 2.92157 15.75 3.75 15.75Z"
            stroke="#6B7280"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const ClockIcon: React.FC = () => (
    <svg width="16" height="16" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M9 6V9L11.25 11.25M15.75 9C15.75 12.7279 12.7279 15.75 9 15.75C5.27208 15.75 2.25 12.7279 2.25 9C2.25 5.27208 5.27208 2.25 9 2.25C12.7279 2.25 15.75 5.27208 15.75 9Z"
            stroke="#6B7280"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

// "Insurance to re-check" 行的紧迫度 tag（设计规则；日界按 provider 时区算）：
// 同日 <4h → "Visit in {n} hours" 红；同日 ≥4h → "Visit today" 红；
// 1-7 天 → "Visit in {n} days" 黄；8-30 天 → "Visit in over a week" 灰；>30 天 → "Visit in over a month" 灰
const recheckTag = (start: dayjs.Dayjs, now: dayjs.Dayjs): { label: string; cls: string } => {
    const dayDiff = start.startOf('day').diff(now.startOf('day'), 'day');
    if (dayDiff <= 0) {
        const hours = Math.max(1, Math.ceil(start.diff(now, 'minute') / 60));
        if (hours < 4) return { label: `Visit in ${hours} hour${hours === 1 ? '' : 's'}`, cls: 'tagRed' };
        return { label: 'Visit today', cls: 'tagRed' };
    }
    if (dayDiff <= 7) return { label: `Visit in ${dayDiff} day${dayDiff === 1 ? '' : 's'}`, cls: 'tagYellow' };
    if (dayDiff <= 30) return { label: 'Visit in over a week', cls: 'tagGray' };
    return { label: 'Visit in over a month', cls: 'tagGray' };
};

const serviceTypeMap = {
    [TextVisitServiceType.MEDICATION_REFILL]: {
        title: 'medication refill',
    },
    [TextVisitServiceType.PRESCRIPTION_REFILL]: {
        title: 'prescription refill',
    },
    [TextVisitServiceType.MEDICATION_CHANGE]: {
        title: 'Medication change',
    },
    [TextVisitServiceType.FOLLOW_UP_CONSULTATION]: {
        title: 'Follow-up consultation',
    },
    [TextVisitServiceType.OTHER]: {
        title: 'Other care needs',
    },
};

interface TodoListModalProps {
    open: boolean;
    onClose: () => void;
    onDataUpdate?: () => void; // 当数据更新时通知父组件
    // 打开时定位的 tab（Home 卡片的 Check/Resume 各自入口）；缺省 inProgress
    initialTab?: TTodoTab;
    // true = 只看 provider appointment、过滤 text visit（Insurance billing 入口用：claim 只由
    // appointment 产生，且实测能把列表从 ~11.5s 提到 ~1.5s）。Home 不传，行为不变。
    // 该入口是 claims 场景，不渲染 Insurance to re-check tab
    appointmentOnly?: boolean;
}

const TodoListModal: React.FC<TodoListModalProps> = ({ open, onClose, onDataUpdate, initialTab, appointmentOnly }) => {
    const [activeTab, setActiveTab] = useState<TTodoTab>('inProgress');
    const navigate = useNavigate();
    const [, myTimezone] = useTimezone();

    // Insurance to re-check（§2.7）：一次全量，后端已按 startAt ASC 排
    const [recheckItems, setRecheckItems] = useState<TRecheckAppointmentVo[]>([]);
    const [recheckLoading, setRecheckLoading] = useState(false);

    // Drawer 状态
    const [showVideoVisitUnSubmittedDrawer, setShowVideoVisitUnSubmittedDrawer] = useState(false);
    const [showTextVisitUnSubmittedDrawer, setShowTextVisitUnSubmittedDrawer] = useState(false);
    const [showTextVisitSubmittedDrawer, setShowTextVisitSubmittedDrawer] = useState(false);
    const [selectedAppointmentId, setSelectedAppointmentId] = useState<number>();
    const [selectedTextVisitUuid, setSelectedTextVisitUuid] = useState<string>();

    // 获取counts数据
    const [counts, setCounts] = useState({ inProgressCount: 0, overdueCount: 0 });

    // 治疗计划项列表数据（后端分页）
    const [inProgressItems, setInProgressItems] = useState<TreatmentPlanItemVo[]>([]);
    const [overdueItems, setOverdueItems] = useState<TreatmentPlanItemVo[]>([]);
    const [loadingItems, setLoadingItems] = useState(false);

    // 后端分页状态
    const [inProgressPage, setInProgressPage] = useState(1);
    const [overduePage, setOverduePage] = useState(1);
    const [inProgressTotal, setInProgressTotal] = useState(0);
    const [overdueTotal, setOverdueTotal] = useState(0);
    const pageSize = 10; // 每页显示10条

    // 获取counts数据
    const fetchCounts = async () => {
        try {
            const result = await getTreatmentPlanAppointmentCounts(undefined, appointmentOnly);
            if (!result.error && result.data?.data) {
                setCounts({
                    inProgressCount: result.data.data.inProgressCount,
                    overdueCount: result.data.data.overdueCount,
                });
            }
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Failed to fetch counts:', error);
        }
    };

    // 获取治疗计划项列表（后端分页）
    const fetchItems = async (status: 'IN_PROGRESS' | 'OVERDUE', page: number = 1) => {
        setLoadingItems(true);
        try {
            const params: TTreatmentPlanAppointmentQueryReq = {
                status,
                orderKey: 'START_AT',
                orderType: 'ASC',
                current: page,
                size: pageSize,
                appointmentOnly: appointmentOnly || undefined,
            };

            const res = await getTreatmentPlanItemList(params);
            const pageData = res?.data?.data;
            const items = pageData?.list || [];
            const total = pageData?.totalRecords || 0;

            if (status === 'IN_PROGRESS') {
                setInProgressItems(items);
                setInProgressTotal(total);
            } else {
                setOverdueItems(items);
                setOverdueTotal(total);
            }
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error(`Failed to fetch ${status} items:`, error);
        } finally {
            setLoadingItems(false);
        }
    };

    const fetchRecheck = async () => {
        setRecheckLoading(true);
        try {
            setRecheckItems(await getRecheckAppointments());
        } finally {
            setRecheckLoading(false);
        }
    };

    useEffect(() => {
        if (open) {
            // appointmentOnly（claims 入口）没有 recheck tab，落到 inProgress
            setActiveTab(initialTab && !(appointmentOnly && initialTab === 'recheck') ? initialTab : 'inProgress');
            fetchCounts();
            fetchItems('IN_PROGRESS', 1);
            fetchItems('OVERDUE', 1);
            if (!appointmentOnly) fetchRecheck();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // 当tab切换时，重置分页
    useEffect(() => {
        if (open) {
            if (activeTab === 'inProgress') {
                setInProgressPage(1);
            } else {
                setOverduePage(1);
            }
        }
    }, [activeTab, open]);

    // 处理分页变化（后端分页，需要重新请求数据）
    const handlePageChange = (page: number, isOverdueTab: boolean) => {
        if (isOverdueTab) {
            setOverduePage(page);
            fetchItems('OVERDUE', page);
        } else {
            setInProgressPage(page);
            fetchItems('IN_PROGRESS', page);
        }
    };

    // 处理按钮点击：根据 itemType 打开对应的 drawer
    const handleButtonClick = (item: TreatmentPlanItemVo) => {
        if (item.itemType === 'TEXT_VISIT') {
            // TEXT_VISIT: 根据是否已提交决定打开哪个 drawer
            const { textVisit } = item;
            if (textVisit?.textVisitUuid) {
                setSelectedTextVisitUuid(textVisit.textVisitUuid);
                // 如果已经提交，打开 TextVisitSubmittedDrawer 查看详情
                // 如果未提交，打开 TextVisitUnSubmittedDrawer 继续处理
                if (textVisit.providerTreatmentSubmitted) {
                    setShowTextVisitSubmittedDrawer(true);
                } else {
                    setShowTextVisitUnSubmittedDrawer(true);
                }
            }
            onClose();
        } else if (item.itemType === 'APPOINTMENT') {
            // APPOINTMENT: 打开 VideoVisitUnSubmittedDrawer
            const { appointment } = item;
            if (appointment?.id) {
                setSelectedAppointmentId(appointment.id);
                setShowVideoVisitUnSubmittedDrawer(true);
            }
            onClose();
        }
    };

    // 渲染列表项（后端分页）
    const renderItems = (isOverdueTab: boolean) => {
        const items = isOverdueTab ? overdueItems : inProgressItems;
        const currentPage = isOverdueTab ? overduePage : inProgressPage;
        const total = isOverdueTab ? overdueTotal : inProgressTotal;

        if (loadingItems) {
            return (
                <div className={s.loadingContainer}>
                    <Spin />
                </div>
            );
        }

        if (items.length === 0) {
            return <div className={s.emptyState}>No {isOverdueTab ? 'overdue' : 'in-progress'} visit notes</div>;
        }

        return (
            <>
                <div className={s.tabContent}>
                    {items.map((item) => {
                        if (item.itemType === 'TEXT_VISIT') {
                            const { textVisit } = item;
                            if (!textVisit) return null;

                            const isFollowUpCase = textVisit.serviceType !== TextVisitServiceType.INITIAL_VISIT;
                            const conditionName = isFollowUpCase
                                ? serviceTypeMap[textVisit.serviceType as keyof typeof serviceTypeMap]?.title
                                : textVisit.conditions;
                            const isRefillVisit =
                                textVisit.serviceType === TextVisitServiceType.MEDICATION_REFILL ||
                                textVisit.serviceType === TextVisitServiceType.PRESCRIPTION_REFILL;

                            const baseName = `${conditionName}: Text visit`;
                            const appointmentName =
                                isRefillVisit && textVisit.specialty ? `${textVisit.specialty} ${baseName}` : baseName;
                            const startAt = textVisit.assignedProviderAt || textVisit.createdAt || '';
                            const baseDate = textVisit.assignedProviderAt || textVisit.createdAt;
                            const endAt = baseDate
                                ? new Date(new Date(baseDate).getTime() + 15 * 24 * 60 * 60 * 1000).toISOString()
                                : textVisit.createdAt;
                            const duration = 0;
                            const hasTextVisitTreatment = textVisit.hasTextVisitTreatment || false;
                            const treatmentPlanText = textVisit.aiSummary;
                            const attachmentsCount = textVisit.attachmentCount || 0;
                            const prescriptions =
                                textVisit.prescriptions && Array.isArray(textVisit.prescriptions)
                                    ? (textVisit.prescriptions as TPrescriptionListItem[])
                                    : undefined;

                            return (
                                <TreatmentPlanCard
                                    key={`textVisit-${textVisit.id}`}
                                    appointmentName={appointmentName}
                                    startAt={startAt}
                                    endAt={endAt}
                                    duration={duration}
                                    hasTextVisitTreatment={hasTextVisitTreatment}
                                    treatmentPlanText={treatmentPlanText}
                                    attachmentsCount={attachmentsCount}
                                    prescriptions={prescriptions}
                                    submittedAt={textVisit.assignedProviderAt}
                                    lastUpdatedAt={textVisit.updatedAt}
                                    status={isOverdueTab ? 'OVERDUE' : 'IN_PROGRESS'}
                                    isDismissed={false}
                                    itemType="TEXT_VISIT"
                                    isRefillVisit={isRefillVisit}
                                    onButtonClick={() => handleButtonClick(item)}
                                />
                            );
                        } else {
                            const { appointment } = item;
                            if (!appointment) return null;

                            const treatmentPlanText = appointment.textVisitTreatmentVo
                                ? (appointment.textVisitTreatmentVo as { treatmentPlan?: string })?.treatmentPlan
                                : undefined;
                            const attachmentsCount = appointment.attachmentCount || 0;
                            const prescriptions =
                                appointment.prescriptions && Array.isArray(appointment.prescriptions)
                                    ? (appointment.prescriptions as TPrescriptionListItem[])
                                    : undefined;
                            const isRefillVisit =
                                appointment.serviceType === TextVisitServiceType.MEDICATION_REFILL ||
                                appointment.serviceType === TextVisitServiceType.PRESCRIPTION_REFILL;

                            return (
                                <TreatmentPlanCard
                                    key={`appointment-${appointment.id}`}
                                    appointmentName={appointment.appointmentName}
                                    startAt={appointment.startAt}
                                    endAt={appointment.endAt}
                                    duration={appointment.duration}
                                    hasTextVisitTreatment={appointment.hasTextVisitTreatment || false}
                                    treatmentPlanText={treatmentPlanText}
                                    attachmentsCount={attachmentsCount}
                                    prescriptions={prescriptions}
                                    status={isOverdueTab ? 'OVERDUE' : 'IN_PROGRESS'}
                                    isDismissed={false}
                                    itemType="APPOINTMENT"
                                    isRefillVisit={isRefillVisit}
                                    onButtonClick={() => handleButtonClick(item)}
                                />
                            );
                        }
                    })}
                </div>
                {total > pageSize && (
                    <div className={s.pagination}>
                        <Pagination
                            current={currentPage}
                            total={total}
                            pageSize={pageSize}
                            onChange={(page) => handlePageChange(page, isOverdueTab)}
                            showSizeChanger={false}
                            showQuickJumper={false}
                        />
                    </div>
                )}
            </>
        );
    };

    // "Check eligibility"：只跳转客户详情页 Insurance & payment 子 tab，不自动触发 check（已拍板）。
    // appointmentId 一起带过去：详情页那次 Run check 走 per-appointment 的 recheck 接口，
    // 而不是按保险行的手动 check。check 完成后后端清 action-state，行下次打开自然消失
    const handleCheckEligibility = (item: TRecheckAppointmentVo) => {
        onClose();
        navigate(
            `${PATH.DASHBOARD}/${PATH.PROSPECTS_DETAIL}/${item.clientId}?subTab=2&recheckAppointmentId=${item.appointmentId}`,
        );
    };

    const renderRecheckItems = () => {
        if (recheckLoading) {
            return (
                <div className={s.loadingContainer}>
                    <Spin />
                </div>
            );
        }
        if (recheckItems.length === 0) {
            return <div className={s.emptyState}>No visits to re-check</div>;
        }
        const tz = myTimezone?.value as string | undefined;
        const now = tz ? dayjs().tz(tz) : dayjs();
        return (
            <div className={s.tabContent}>
                {recheckItems.map((item) => {
                    // 文档口径：ISO 无时区 = UTC → provider 时区展示
                    const start = tz ? dayjs.utc(item.startAt).tz(tz) : dayjs.utc(item.startAt).local();
                    const end = start.add(item.duration || 0, 'minute');
                    const patientName = `${item.patientFirstName || ''} ${item.patientLastName || ''}`.trim();
                    const tag = recheckTag(start, now);
                    return (
                        <div className={s.recheckCard} key={item.appointmentId}>
                            <div className={s.recheckMain}>
                                <div className={s.recheckTitle}>
                                    {patientName ? `${patientName} - ` : ''}
                                    {item.appointmentName}
                                </div>
                                <div className={s.recheckMeta}>
                                    <span>
                                        <CalendarIcon /> {start.format('dddd, MMMM D, YYYY')}
                                    </span>
                                    <span>
                                        <ClockIcon /> {start.format('h:mm')} - {end.format('h:mm a')}
                                        {tz ? ` (${tz})` : ''}
                                        {item.duration ? ` | ${item.duration} mins` : ''}
                                    </span>
                                </div>
                            </div>
                            <div className={s.recheckSide}>
                                <span className={`${s.recheckTag} ${s[tag.cls]}`}>{tag.label}</span>
                                <Button size="small" onClick={() => handleCheckEligibility(item)}>
                                    Check eligibility
                                </Button>
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    const tabItems = [
        // Insurance to re-check 恒为第一个 tab（claims 入口 appointmentOnly 不渲染）
        ...(appointmentOnly
            ? []
            : [
                  {
                      key: 'recheck',
                      label: `Insurance to re-check (${recheckItems.length})`,
                      children: renderRecheckItems(),
                  },
              ]),
        {
            key: 'inProgress',
            label: `In-progress visit notes (${counts.inProgressCount})`,
            children: renderItems(false),
        },
        {
            key: 'overdue',
            label: `Overdue visit notes (${counts.overdueCount})`,
            children: renderItems(true),
        },
    ];

    return (
        <>
            <Modal
                open={open}
                onCancel={onClose}
                footer={null}
                width={800}
                className={s.modal}
                closeIcon={null}
                destroyOnClose
            >
                <div className={s.modalContent}>
                    <div className={s.header}>
                        <h3 className={s.title}>To-do list</h3>
                    </div>
                    <Tabs
                        activeKey={activeTab}
                        onChange={(key) => setActiveTab(key as TTodoTab)}
                        items={tabItems}
                        className={s.tabs}
                    />
                </div>
            </Modal>
            {showVideoVisitUnSubmittedDrawer && (
                <VideoVisitUnSubmittedDrawer
                    show={showVideoVisitUnSubmittedDrawer}
                    onClose={() => {
                        setShowVideoVisitUnSubmittedDrawer(false);
                        setSelectedAppointmentId(undefined);
                    }}
                    appointmentId={selectedAppointmentId}
                    onSave={() => {
                        // 刷新数据
                        fetchItems('IN_PROGRESS', inProgressPage);
                        fetchItems('OVERDUE', overduePage);
                        fetchCounts();
                        onDataUpdate?.();
                    }}
                />
            )}
            {/* TextVisitUnSubmittedDrawer for TEXT_VISIT (未提交的) - 放在 Modal 外部以确保层级更高 */}
            {showTextVisitUnSubmittedDrawer && (
                <TextVisitUnSubmittedDrawer
                    show={showTextVisitUnSubmittedDrawer}
                    onClose={() => {
                        setShowTextVisitUnSubmittedDrawer(false);
                        setSelectedTextVisitUuid(undefined);
                    }}
                    textVisitUuid={selectedTextVisitUuid}
                    onSave={() => {
                        // 刷新数据
                        fetchItems('IN_PROGRESS', inProgressPage);
                        fetchItems('OVERDUE', overduePage);
                        fetchCounts();
                        onDataUpdate?.();
                    }}
                    onNext={(data) => {
                        // 处理下一个 case（如果需要的话）
                        const { nextTextVisitUuid, inProgress } = data;
                        if (nextTextVisitUuid) {
                            setSelectedTextVisitUuid(nextTextVisitUuid);
                            // 如果是在进行中的，保持 TextVisitUnSubmittedDrawer 打开；否则可以关闭
                            if (!inProgress) {
                                setShowTextVisitUnSubmittedDrawer(false);
                            }
                        }
                    }}
                />
            )}
            {showTextVisitSubmittedDrawer && (
                <TextVisitSubmittedDrawer
                    open={showTextVisitSubmittedDrawer}
                    onClose={() => {
                        setShowTextVisitSubmittedDrawer(false);
                        setSelectedTextVisitUuid(undefined);
                    }}
                    textVisitUuid={selectedTextVisitUuid}
                />
            )}
        </>
    );
};

export default TodoListModal;
